import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { takeUntil, map, startWith } from 'rxjs/operators';
import { CompaniesFacade } from '../../store/companies/companies.facade';
import { CompanyListItem } from '../../core';
import { AuthService } from '../../core/services/auth.service';
import { Permissions } from '../../core/enums';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { BaseTableComponent } from '../../shared/components/base-table/base-table.component';
import {
  ColumnVisibilityToggleComponent,
  GridColumn,
} from '../../shared/components/column-visibility-toggle/column-visibility-toggle.component';
import {
  UkgTableData,
  transformToTableData,
  nullishFallback,
} from '../../shared/utils/table-data-transformer';

@Component({
  selector: 'app-company',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HasPermissionDirective,
    ColumnVisibilityToggleComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyComponent extends BaseTableComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly companiesFacade = inject(CompaniesFacade);
  private readonly authService = inject(AuthService);
  override readonly cdr = inject(ChangeDetectorRef);
  readonly Permissions = Permissions;

  override Math = Math;

  protected readonly columnVisibilityKeyName = 'companyColumnVisibility';

  columns: GridColumn[] = [
    { field: 'arNumber', header: 'AR Number', width: '120px' },
    { field: 'name', header: 'Name', width: '200px' },
    { field: 'type', header: 'Type', width: '150px' },
    {
      field: 'preferredPrintSite',
      header: 'Preferred Print Site',
      width: '200px',
    },
  ].map(col => ({ ...col, sortable: true, visible: true }));

  get visibleColumns(): GridColumn[] {
    return this.columnVisibilityService.getVisibleColumns(this.columns);
  }

  // Reactive state from facade
  readonly companies$ = this.companiesFacade.companies$;
  readonly loading$ = this.companiesFacade.loading$;
  readonly error$ = this.companiesFacade.error$;
  readonly sortBy$ = this.companiesFacade.sortBy$;
  readonly sortOrder$ = this.companiesFacade.sortOrder$;

  // Transform companies data to Ignite table format
  tableData$: Observable<UkgTableData[]> = this.companiesFacade.companies$.pipe(
    map(pagedResult =>
      transformToTableData(pagedResult, company => ({
        arNumber: company.arNumber,
        name: company.name,
        type: nullishFallback(company.type),
        preferredPrintSite: nullishFallback(company.preferredPrintSite),
      }))
    )
  );

  // Filtered and sorted table data
  filteredTableData$: Observable<UkgTableData[]> = combineLatest([
    this.tableData$,
    this.columnFiltersSubject
      .asObservable()
      .pipe(startWith(this.columnFilters)),
  ]).pipe(
    map(([data, filters]) => {
      const filtered = this.filterTableData(data, filters);
      return this.sortTableData(
        filtered,
        this.currentSortField,
        this.currentSortDirection
      );
    })
  );

  override toggleColumnVisibility(): void {
    super.toggleColumnVisibility(this.columns);
  }

  override resetColumnVisibility(): void {
    super.resetColumnVisibility(this.columns);
  }

  protected override loadColumnVisibility(): void {
    super.loadColumnVisibility(this.columns);
  }

  ngOnInit(): void {
    this.loadColumnVisibility();
    this.initializeSearch(term => {
      this.companiesFacade.searchCompanies(term);
    });

    this.companies$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result !== null) {
        this.hasLoadedOnce = true;
      }
    });

    combineLatest([this.sortBy$, this.sortOrder$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([sortBy, sortOrder]) => {
        this.currentSortField = sortBy || null;
        this.currentSortDirection = (sortOrder as 'asc' | 'desc') || null;
        this.cdr.markForCheck();
      });

    this.companiesFacade.loadCompanies();
  }

  get canAddOrEditCompanies(): boolean {
    return this.authService.hasPermissionSync([
      Permissions.AccessToAddOrEditCompanies,
    ]);
  }

  onAddCompany(): void {
    this.router.navigate(['/company/add']);
  }

  onTableSort(sortDetail: { key: string; isAsc: boolean | 'none' }): void {
    this.companiesFacade.sort(sortDetail.key);
  }

  getSortIcon(field: string): string {
    if (this.currentSortField !== field || !this.currentSortDirection)
      return '';
    return this.currentSortDirection === 'asc' ? '↑' : '↓';
  }

  handleTableClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Find the closest table row
    const row = target.closest('tr');
    if (!row) {
      return;
    }

    // Skip header rows
    if (row.parentElement?.tagName === 'THEAD') {
      return;
    }

    // Get all cells in the row
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells.length === 0) {
      return;
    }

    // The first cell (after checkbox) should contain the AR number
    const firstCell = cells[0];
    const arNumberText = firstCell?.textContent?.trim();

    if (arNumberText) {
      // Find company by AR number and navigate to edit
      this.companies$.pipe(takeUntil(this.destroy$)).subscribe(pagedResult => {
        if (!pagedResult?.items) {
          return;
        }

        const company = pagedResult.items.find(
          c => c.arNumber === arNumberText
        );
        if (company) {
          this.onEditCompany(company);
        }
      });
    }
  }

  override onPageChange(page: number): void {
    this.companiesFacade.changePage(page);
  }

  override onPageSizeChange(pageSize: number): void {
    this.companiesFacade.changePageSize(pageSize);
  }

  override onRetry(): void {
    this.companiesFacade.loadCompanies();
  }

  onEditCompany(company: CompanyListItem): void {
    if (!this.canAddOrEditCompanies) {
      return;
    }
    this.router.navigate(['/company/edit', company.arNumber]);
  }

  onRowClick(company: CompanyListItem, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Skip if clicking on checkbox or button
    if (
      target.tagName === 'INPUT' ||
      target.closest('.checkbox-column') ||
      target.closest('ukg-icon-button')
    ) {
      return;
    }
    this.onEditCompany(company);
  }

  onRowKeydown(company: CompanyListItem): void {
    this.onEditCompany(company);
  }
}
